import Api, { ErrorMode, FetchMethodType } from './Api';

export interface SendSmsCodeData {
  phoneNumber: string;
}

export interface SendEmailCodeData {
  phoneNumber: string;
  email: string;
}

export interface SendSmsCodeForApplicationIdData {
  applicationId: string;
}

export interface SendSmsCodeResponse {
  phoneId: string;
}

export interface HStreamCallbackData {
  code: string;
}

export interface HStreamTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

export interface HStreamUserInfo {
  sub: string;
  identities: string;
  emailVerified: string;
  givenName: string;
  familyName: string;
  hstreamId: string;
  email: string;
  userId: string;
  username: string;
}

export interface HStreamCallbackResponse {
  tokens: HStreamTokens;
  userInfo: HStreamUserInfo;
}

export interface SendEmailCodeResponse {
  emailId: string;
}

export interface AuthOtpData {
  phoneOrEmailId: string;
  code: string;
  phoneNumber?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  credential?: string;
}

export type AuthOtpResponse =
  | {
      verified: false;
    }
  | {
      verified: true;
      sessionToken: string;
      firstName?: string;
      lastName?: string;
      credential?: string;
      canReapply?: boolean;
    };

export interface ReapplyData {
  sessionToken: string;
  firstName: string;
  lastName: string;
  credential?: string;
}

export interface ReapplyAddress {
  street: string;
  city: string;
  stateOrProvince: string;
  zipOrPostalCode: string;
  country: string;
}

export interface ReapplyResponse {
  email: string;
  address: ReapplyAddress;
  primaryEmployerName: string;
  notEmployed: boolean;
  additionalIncome?: number;
  totalAnnualIncome: number;
  credentialsAcquisitionDate?: string;
  dateOfBirth: string;
  last4SSN: string;
}

export enum ErrorType {
  DifferentEmailSession = 'different_email_session',
  DifferentPhoneNumberSession = 'different_phone_number_session',
  UnsubscribedPhoneNumber = 'unsubscribed_phone_number',
}

export type AuthOtpResponseError = {
  status: number;
  error: string;
  errorType: ErrorType;
};

export interface AuthSessionData {
  sessionToken: string;
}

export type AuthSessionResponse =
  | {
      authenticated: false;
    }
  | {
      authenticated: true;
      phoneNumber: string;
    };

export interface AuthApi {
  sendSmsCode(data: SendSmsCodeData): Promise<SendSmsCodeResponse>;
  sendSmsCodeForApplicationId(data: SendSmsCodeForApplicationIdData): Promise<SendSmsCodeResponse>;
  sendEmailCode(data: SendEmailCodeData): Promise<SendEmailCodeResponse>;
  authOtp(data: AuthOtpData): Promise<AuthOtpResponse>;
  reapply(data: ReapplyData): Promise<ReapplyResponse>;
  authSession(data: AuthSessionData): Promise<AuthSessionResponse>;
  hstreamCallback(data: HStreamCallbackData): Promise<HStreamCallbackResponse>;
}

export default class AuthRestApi extends Api implements AuthApi {
  async sendSmsCode(data: SendSmsCodeData): Promise<SendSmsCodeResponse> {
    return this.fetch<SendSmsCodeResponse>('/auth/sendSmsCode', {
      method: FetchMethodType.POST,
      body: data,
      errorMode: ErrorMode.HideError,
    });
  }

  async sendSmsCodeForApplicationId(data: SendSmsCodeForApplicationIdData): Promise<SendSmsCodeResponse> {
    return this.fetch<SendSmsCodeResponse>('/auth/sendSmsCodeForApplicationId', {
      method: FetchMethodType.POST,
      body: data,
      errorMode: ErrorMode.HideError,
    });
  }

  async authOtp(data: AuthOtpData): Promise<AuthOtpResponse> {
    return this.fetch<AuthOtpResponse>(`/auth/authOtp`, {
      method: FetchMethodType.POST,
      body: data,
      errorMode: ErrorMode.HideError,
      redirectToLogin: false,
    });
  }

  async reapply(data: ReapplyData): Promise<ReapplyResponse> {
    return this.fetch<ReapplyResponse>('/auth/reapply', {
      method: FetchMethodType.POST,
      body: data,
      errorMode: ErrorMode.HideError,
      redirectToLogin: false,
      maxRetries: 0,
    });
  }

  async sendEmailCode(data: SendEmailCodeData): Promise<SendEmailCodeResponse> {
    return this.fetch<SendEmailCodeResponse>('/auth/sendEmailCode', {
      method: FetchMethodType.POST,
      body: data,
    });
  }

  async authSession(data: AuthSessionData): Promise<AuthSessionResponse> {
    return this.fetch<AuthSessionResponse>(`/auth/authSession?sessionToken=${encodeURIComponent(data.sessionToken)}`, {
      method: FetchMethodType.GET,
    });
  }

  async hstreamCallback(data: HStreamCallbackData): Promise<HStreamCallbackResponse> {
    return this.fetch<HStreamCallbackResponse>('/auth/hstream/callback', {
      method: FetchMethodType.POST,
      body: data,
      errorMode: ErrorMode.DisplayActualMessage,
    });
  }
}
